import { useMutation, useQueryClient } from '@tanstack/react-query';

import { patchUserProductMutationFn } from '@/queries/products';
import { Product } from '@/queries/products/types';

import { useCheckoutInterception } from './CheckoutHandlerContext';
import useProductCheckout from './useProductCheckout';
import { handlePatchUserProductSuccess } from './utils';

interface UseCheckoutOrUpgradeOptions {
  entryPoint?: string; // for analytics only
  successPath?: string;
  cancelPath?: string;
  onUpgradeSuccess?: () => void;
}

/**
 * Checkout-or-upgrade behavior for a purchasable product button. When the API marks the product
 * with `eligibility.tierChangeableUserProductId`, the click performs an in-place prorated
 * `tierChange` on that subscription instead of a fresh Stripe checkout. The tierChange is immediate
 * and prorated, so only use this for the upgrade direction (e.g. Explorer → Educator) — a downgrade
 * (next-cycle, no proration) must not be driven through this hook.
 */
const useCheckoutOrUpgrade = (product: Product | undefined, options: UseCheckoutOrUpgradeOptions = {}) => {
  const { entryPoint, successPath, cancelPath, onUpgradeSuccess } = options;
  const queryClient = useQueryClient();
  const interception = useCheckoutInterception();

  const { start: startCheckout, isPending: isCheckingOut } = useProductCheckout();

  const {
    mutate: patchUserProduct,
    isPending: isChangingTier,
    isSuccess: isUpgradeSuccess,
  } = useMutation({
    mutationFn: patchUserProductMutationFn,
    onSuccess: () => {
      handlePatchUserProductSuccess(queryClient)();
      onUpgradeSuccess?.();
      interception?.onUpgraded?.();
    },
  });

  const isUpgrade = !!product?.eligibility?.tierChangeableUserProductId;

  return {
    isUpgrade,
    isUpgradeSuccess,
    label: isUpgradeSuccess ? 'Upgraded ✔' : isUpgrade ? 'Upgrade Plan' : 'Select Plan',
    disabled: isCheckingOut || isChangingTier || isUpgradeSuccess || (!product?.eligibility?.allowed && !isUpgrade),
    loading: isCheckingOut || isChangingTier,
    onClick: () => {
      if (!product) return;
      if (product.eligibility?.tierChangeableUserProductId) {
        patchUserProduct({
          userProductId: product.eligibility.tierChangeableUserProductId,
          action: 'tierChange',
          targetProductId: product._id,
          targetPriceId: product.price.priceId,
        });
      } else {
        startCheckout({
          productId: product._id,
          priceId: product.price.priceId,
          successPath,
          cancelPath,
          productLookupKey: product.productLookupKey,
          entryPoint,
        });
      }
    },
  };
};

export default useCheckoutOrUpgrade;
